import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarClock,
  CalendarDays,
  Heart,
  MapPin,
  Maximize,
  Navigation,
  Send,
  Link as LinkIcon,
  Share2,
  ShieldCheck,
  Store,
} from "lucide-react";

import { toast } from "sonner";
import { partnerSlug } from "@/lib/partner-public";

import { TopHeader } from "@/components/TopHeader";
import { BottomNavigation } from "@/components/BottomNavigation";
import { ImageCarousel } from "@/components/ImageCarousel";
import { StatusBadge } from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { EnquiryForm } from "@/components/EnquiryForm";
import { VisitRequestForm } from "@/components/VisitRequestForm";
import { fetchPropertyDetail, type PropertyDetail } from "@/lib/property-details";
import { recordListingView } from "@/lib/analytics";
import { recordView } from "@/lib/recently-viewed";
import { isFavourite, toggleFavourite } from "@/lib/favourites";
import { useAuth } from "@/lib/auth";
import { haversineKm } from "@/lib/map-search";
import { ContactPanel } from "@/components/ContactPanel";
import { ReviewsSection } from "@/components/ReviewsSection";
import { ShareDialog } from "@/components/ShareDialog";
import { CompareButton } from "@/components/CompareButton";
import { ReportDialog } from "@/components/ReportDialog";
import { CompareBar } from "@/components/CompareBar";

import { publicListingUrl, copyToClipboard } from "@/lib/share";

export const Route = createFileRoute("/property/$id")({
  head: () => ({
    meta: [
      { title: "Property details · Landoo" },
      {
        name: "description",
        content: "Photos, price, amenities, location and partner details for this Landoo listing.",
      },
      { property: "og:title", content: "Property details · Landoo" },
      {
        property: "og:description",
        content: "Photos, price, amenities, location and partner details for this Landoo listing.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  notFoundComponent: () => <NotFound />,
  errorComponent: () => <NotFound message="Something went wrong loading this property." />,
  component: PropertyPage,
});

function PropertyPage() {
  const { id } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ["property-detail", id],
    queryFn: () => fetchPropertyDetail(id),
  });

  return (
    <div className="flex min-h-screen flex-col bg-cream/30 pb-24 md:pb-0">
      <TopHeader />
      <main className="mx-auto w-full max-w-5xl flex-1 p-4 md:p-6">
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-56 w-full rounded-3xl lg:h-[420px]" />
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-40 w-full rounded-3xl" />
          </div>
        ) : data ? (
          <PropertyDetailView detail={data} />
        ) : (
          <NotFound />
        )}
      </main>
      <BottomNavigation />
    </div>
  );
}

function PropertyDetailView({ detail }: { detail: PropertyDetail }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [fav, setFav] = useState(false);
  const [distanceKm, setDistanceKm] = useState<number | null>(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [visitOpen, setVisitOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => setFav(isFavourite(detail.id)), [detail.id]);

  useEffect(() => {
    recordView({
      id: detail.id,
      title: detail.title,
      price: detail.priceLabel,
      image: detail.images[0] ?? null,
      service: detail.kind,
      city: detail.city,
    });
  }, [detail.id, detail.title, detail.priceLabel, detail.images, detail.kind, detail.city]);

  useEffect(() => {
    if (detail.demo || !detail.ownerId) return;
    void recordListingView({
      listingId: detail.id,
      serviceType: detail.kind,
      ownerId: detail.ownerId,
      viewerId: user?.id ?? null,
    }).catch(() => undefined);
  }, [detail.id, detail.demo, detail.ownerId, detail.kind, user?.id]);

  useEffect(() => {
    if (detail.latitude == null || detail.longitude == null) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setDistanceKm(
          haversineKm(
            { lat: pos.coords.latitude, lng: pos.coords.longitude },
            { lat: detail.latitude as number, lng: detail.longitude as number },
          ),
        ),
      () => setDistanceKm(null),
      { timeout: 8000 },
    );
  }, [detail.latitude, detail.longitude]);

  const posted = useMemo(() => {
    if (!detail.postedAt) return "Recently posted";
    return new Date(detail.postedAt).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, [detail.postedAt]);

  const contactVisible = detail.contact.showNumber || detail.contact.showWhatsapp;

  const fullAddress = useMemo(() => {
    const city = detail.city?.trim();
    const addr = detail.address?.trim() ?? "";
    if (!city) return addr || "—";
    return addr.toLowerCase().includes(city.toLowerCase())
      ? addr
      : [addr, city].filter(Boolean).join(", ");
  }, [detail.address, detail.city]);

  const requireAuth = (message: string) => {
    if (user) return true;
    toast.error(message);
    nav({ to: "/login" });
    return false;
  };

  const onFavourite = () => {
    if (!requireAuth("Sign in to save favourites.")) return;
    const next = toggleFavourite(detail.id);
    setFav(next);
    toast.success(next ? "Saved to favourites." : "Removed from favourites.");
  };

  const onShare = async () => {
    const url = publicListingUrl(detail.id);
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: detail.title, text: detail.priceLabel, url });
        return;
      }
    } catch {
      /* user dismissed the native share sheet */
    }
    setShareOpen(true);
  };

  const onCopyLink = async () => {
    const ok = await copyToClipboard(publicListingUrl(detail.id));
    toast[ok ? "success" : "error"](ok ? "Link copied to clipboard." : "Could not copy the link.");
  };

  const mapSrc =
    detail.latitude != null && detail.longitude != null
      ? `https://www.google.com/maps?q=${detail.latitude},${detail.longitude}&z=15&output=embed`
      : `https://www.google.com/maps?q=${encodeURIComponent(fullAddress)}&z=13&output=embed`;

  const directionsHref =
    detail.latitude != null && detail.longitude != null
      ? `https://www.google.com/maps/dir/?api=1&destination=${detail.latitude},${detail.longitude}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;

  return (
    <article className="space-y-4">
      <ImageCarousel images={detail.images} alt={detail.title} />

      {/* Title / price */}
      <section className="rounded-3xl border border-border bg-card p-4 shadow-soft sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge purpose={detail.kind} />
          <span className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-brown-dark">
            {detail.propertyType}
          </span>
          {detail.verified && (
            <span className="inline-flex items-center gap-1 rounded-full bg-rent/15 px-2.5 py-1 text-[11px] font-semibold text-rent">
              <ShieldCheck className="h-3.5 w-3.5" /> Verified
            </span>
          )}
          {detail.demo && (
            <span className="rounded-full bg-brand-soft px-2.5 py-1 text-[11px] font-semibold text-brown-dark">
              Sample listing
            </span>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-bold leading-tight text-brown-dark sm:text-2xl">
              {detail.title}
            </h1>
            <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 shrink-0" />
              <span className="truncate">{fullAddress}</span>
            </p>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-coral sm:text-2xl">{detail.priceLabel}</div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {detail.kind === "parking" ? "Parking rate" : "Asking price"}
            </div>
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Fact
            icon={<Maximize className="h-4 w-4" />}
            label="Area"
            value={detail.areaSqft ? `${detail.areaSqft} sqft` : "—"}
          />
          <Fact
            icon={<Navigation className="h-4 w-4" />}
            label="Distance"
            value={distanceKm != null ? `${distanceKm.toFixed(1)} km` : "Enable GPS"}
          />
          <Fact icon={<CalendarDays className="h-4 w-4" />} label="Posted" value={posted} />
          <Fact
            icon={<Store className="h-4 w-4" />}
            label="Partner"
            value={detail.partnerName ?? "Landoo Partner"}
          />
        </dl>

        {/* Actions */}
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-6">
          <ActionButton
            icon={<Heart className={`h-4 w-4 ${fav ? "fill-coral text-coral" : ""}`} />}
            label={fav ? "Saved" : "Favourite"}
            onClick={onFavourite}
          />
          <CompareButton id={detail.id} variant="button" />
          <ActionButton icon={<Share2 className="h-4 w-4" />} label="Share" onClick={onShare} />
          <ActionButton
            icon={<LinkIcon className="h-4 w-4" />}
            label="Copy Link"
            onClick={onCopyLink}
          />

          <ActionButton
            icon={<Send className="h-4 w-4" />}
            label="Send Enquiry"
            primary
            onClick={() => {
              if (!requireAuth("Sign in to send an enquiry.")) return;
              if (!detail.enquiryEnabled) {
                toast.info("Enquiries are disabled for this listing.");
                return;
              }
              setEnquiryOpen(true);
            }}
          />
          <ActionButton
            icon={<CalendarClock className="h-4 w-4" />}
            label="Request Visit"
            primary
            onClick={() => {
              if (!requireAuth("Sign in to request a property visit.")) return;
              setVisitOpen(true);
            }}
          />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-4">
          {/* Description */}
          <Panel title="Description">
            <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
              {detail.description}
            </p>
          </Panel>

          {/* Amenities */}
          <Panel title="Amenities">
            {detail.amenities.length ? (
              <div className="flex flex-wrap gap-1.5">
                {detail.amenities.map((a) => (
                  <span
                    key={a}
                    className="rounded-full bg-brand-soft px-3 py-1 text-xs font-medium text-brown-dark"
                  >
                    {a}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No amenities listed.</p>
            )}
          </Panel>

          {/* Map */}
          <Panel title="Location">
            <div className="overflow-hidden rounded-2xl border border-border">
              <iframe
                title={`Map location of ${detail.title}`}
                src={mapSrc}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="h-56 w-full sm:h-72"
              />
            </div>
            <a
              href={directionsHref}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
            >
              <Navigation className="h-3.5 w-3.5" /> Get directions
            </a>
          </Panel>
        </div>

        <div className="space-y-4">
          {/* Contact visibility */}
          <Panel title="Contact">
            <ContactPanel
              listingId={detail.id}
              ownerId={detail.ownerId}
              service={detail.kind}
              showNumber={detail.contact.showNumber}
              showWhatsapp={detail.contact.showWhatsapp}
              allowUnlock={detail.contact.allowUnlock}
              fallbackPhone={detail.contact.phone}
              fallbackWhatsapp={detail.contact.whatsapp}
            />
          </Panel>

          {/* Partner */}
          <Panel title="Listed by">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-soft text-brown-dark">
                <Store className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-brown-dark">
                  {detail.partnerName ?? "Landoo Partner"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {detail.verified ? "Verified partner" : "Verification in progress"}
                </div>
              </div>
            </div>
            {detail.ownerId && (
              <Link
                to="/p/$slug"
                params={{ slug: partnerSlug(detail.partnerName, detail.ownerId) }}
                className="mt-3 inline-flex rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
              >
                View partner profile
              </Link>
            )}

            <div className="mt-3">
              <ReportDialog
                target={{
                  targetType: "listing",
                  listingId: detail.id,
                  listingKind: detail.kind,
                  serviceType: detail.kind as never,
                  listingTitle: detail.title,
                  partnerId: detail.ownerId ?? null,
                  partnerName: detail.partnerName ?? null,
                }}
                label="Report this listing"
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-border px-3 py-2 text-xs font-semibold text-muted-foreground transition hover:bg-secondary"
              />
            </div>
          </Panel>
        </div>
      </div>

      <div className="mt-4">
        <Panel title="Reviews & Ratings">
          <ReviewsSection
            listingId={detail.id}
            listingKind={detail.kind}
            serviceType={detail.kind as never}
            listingTitle={detail.title}
            partnerId={detail.ownerId}
          />
        </Panel>
      </div>

      <CompareBar className="mb-16 sm:mb-0" />

      <ShareDialog
        listing={{ id: detail.id, title: detail.title, priceLabel: detail.priceLabel }}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />

      {enquiryOpen && <EnquiryDialog detail={detail} onClose={() => setEnquiryOpen(false)} />}

      {visitOpen && <VisitDialog detail={detail} onClose={() => setVisitOpen(false)} />}
    </article>
  );
}

function VisitDialog({ detail, onClose }: { detail: PropertyDetail; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-brown-dark/40 p-0 sm:items-center sm:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Request a property visit"
        className="w-full max-w-md rounded-t-3xl border border-border bg-card p-4 shadow-soft sm:rounded-3xl"
      >
        <h2 className="text-base font-bold text-brown-dark">Request a property visit</h2>
        <p className="mt-1 text-xs text-muted-foreground">{detail.title}</p>
        <VisitRequestForm
          target={{
            id: detail.id,
            title: detail.title,
            serviceType: detail.kind,
            partnerId: detail.ownerId ?? null,
            demo: detail.demo,
          }}
          onDone={onClose}
        />
        <button
          type="button"
          onClick={onClose}
          className="mt-2 w-full rounded-full border border-border px-4 py-2 text-sm font-semibold text-brown-dark transition hover:bg-secondary"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function EnquiryDialog({ detail, onClose }: { detail: PropertyDetail; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-brown-dark/40 p-0 sm:items-center sm:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Send enquiry"
        className="w-full max-w-md rounded-t-3xl border border-border bg-card p-4 shadow-soft sm:rounded-3xl"
      >
        <h2 className="text-base font-bold text-brown-dark">Send enquiry</h2>
        <p className="mt-1 text-xs text-muted-foreground">{detail.title}</p>
        <EnquiryForm
          target={{
            id: detail.id,
            title: detail.title,
            serviceType: detail.kind,
            listingKind: detail.kind,
            partnerId: detail.ownerId ?? null,
            demo: detail.demo,
          }}
          onDone={onClose}
        />
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-3xl border border-border bg-card p-4 shadow-soft sm:p-5">
      <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Fact({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-2xl bg-secondary/60 p-2.5">
      <dt className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground">
        {icon} {label}
      </dt>
      <dd className="mt-0.5 truncate text-sm font-semibold text-brown-dark">{value}</dd>
    </div>
  );
}

function ContactRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2 rounded-2xl bg-secondary/60 px-3 py-2">
      <span className="text-brown-dark">{icon}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="ml-auto text-sm font-semibold text-brown-dark">{value}</span>
    </div>
  );
}

function ActionButton({
  icon,
  label,
  onClick,
  primary,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs font-semibold transition sm:text-sm ${
        primary
          ? "bg-brand text-brown-dark shadow-soft hover:brightness-95"
          : "border border-border text-brown-dark hover:bg-secondary"
      }`}
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}

function NotFound({ message = "This property doesn't exist." }: { message?: string }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6 text-center">
      <div>
        <h1 className="text-2xl font-bold text-brown-dark">Not available</h1>
        <p className="mt-2 text-sm text-muted-foreground">{message}</p>
        <Link
          to="/"
          className="mt-4 inline-flex rounded-full bg-brand px-4 py-2 text-sm font-semibold text-brown-dark shadow-soft"
        >
          Back to map
        </Link>
      </div>
    </div>
  );
}
